import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Leaf, CheckCircle2, Loader2, Star, ThumbsUp, Paperclip, FileText, X } from "lucide-react";
import { toast } from "sonner";

export default function ReferensSvar() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [recommend, setRecommend] = useState(null);
  const [dimAnswers, setDimAnswers] = useState({}); // {id: {text, cannot_assess}}
  const [uploads, setUploads] = useState([]); // [{id, filename}]
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/reference/${token}`)
      .then((r) => { setInfo(r.data); if (r.data.already_responded) setDone(true); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [token]);

  const setDim = (id, patch) => setDimAnswers((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Filen är för stor (max 25 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/reference/${token}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const preview = file.type?.startsWith("image/") ? URL.createObjectURL(file) : "";
      setUploads((u) => [...u, { ...data, preview }]);
      toast.success("Intyg uppladdat");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!message.trim()) { toast.error("Skriv gärna några ord först"); return; }
    setBusy(true);
    try {
      const dims = {};
      Object.entries(dimAnswers).forEach(([id, d]) => {
        if (d.cannot_assess || (d.text || "").trim()) dims[id] = { text: (d.text || "").trim(), cannot_assess: !!d.cannot_assess };
      });
      await api.post(`/reference/${token}/respond`, { message, rating: rating || null, would_recommend: recommend, dimensions: dims });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="hamn-bg min-h-screen flex items-center justify-center p-4 sm:p-6">
      <Card className="w-full max-w-lg rounded-2xl border-border p-6 sm:p-8 md:p-10">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/hamn-logo.png" alt="HAMN" className="h-9 w-9 rounded-lg object-cover" />
          <span className="font-head font-bold text-primary text-lg">HAMN CV Studio</span>
        </div>

        {error && <p className="text-destructive break-words">{error}</p>}
        {!error && !info && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" /></div>}

        {info && done && (
          <div className="text-center py-6" data-testid="reference-done">
            <CheckCircle2 className="h-12 w-12 mx-auto text-[hsl(var(--success))] mb-3" />
            <h1 className="font-head text-2xl font-bold">Tack för din referens!</h1>
            <p className="text-muted-foreground mt-2">Ditt svar har registrerats. Du kan stänga sidan.</p>
          </div>
        )}

        {info && !done && (
          <div>
            <h1 className="font-head text-2xl font-bold">Referens för {info.candidate_name}</h1>
            <p className="text-muted-foreground mt-2 mb-6">
              {info.candidate_name} har angett dig som {info.ref_type?.toLowerCase() || "referens"}
              {info.relation ? ` (${info.relation.toLowerCase()})` : ""}. {info.open_prompt || "Dela gärna några ord om er samarbete."}
            </p>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Din referens</Label>
                <Textarea data-testid="reference-message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder={info.question_focus === "personal" ? "Berätta om hur personen är, t.ex. i vänskap, familj eller sociala sammanhang…" : "Berätta om hur det var att arbeta med personen…"} />
              </div>

              {(info.requested_dimensions || []).length > 0 && (
                <div className="space-y-4 rounded-xl border border-border p-4 bg-muted/30" data-testid="dimension-questions">
                  <p className="text-sm text-muted-foreground">
                    {info.question_focus === "personal"
                      ? `${info.candidate_name} vill gärna att du beskriver några personliga sidor. Skriv gärna ett kort exempel för varje – eller markera om du inte kan bedöma.`
                      : `${info.candidate_name} har bett dig bekräfta några egenskaper från arbetslivet. Skriv gärna ett kort exempel för varje – eller markera om du inte kan bedöma.`}
                  </p>
                  {info.requested_dimensions.map((d) => {
                    const cur = dimAnswers[d.id] || {};
                    return (
                      <div key={d.id} className="space-y-1.5" data-testid={`dimension-${d.id}`}>
                        <Label className="text-sm font-medium">{d.title}</Label>
                        <Textarea
                          rows={2}
                          value={cur.text || ""}
                          disabled={cur.cannot_assess}
                          onChange={(e) => setDim(d.id, { text: e.target.value })}
                          placeholder="Ett kort exempel…"
                          data-testid={`dimension-text-${d.id}`}
                        />
                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={!!cur.cannot_assess} onChange={(e) => setDim(d.id, { cannot_assess: e.target.checked })} data-testid={`dimension-cannot-${d.id}`} />
                          Jag kan inte bedöma detta
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="space-y-2">
                <Label>Helhetsintryck (valfritt)</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rating-${n}`}
                      aria-label={`Betyg ${n}`}
                      className={`flex h-11 w-11 items-center justify-center rounded-lg transition-colors ${n <= rating ? "text-[hsl(var(--warning))]" : "text-muted-foreground"} hover:bg-muted`}>
                      <Star className="h-7 w-7" fill={n <= rating ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Skulle du rekommendera personen? (valfritt)</Label>
                <div className="flex gap-2">
                  <Button type="button" variant={recommend === true ? "default" : "outline"} className="rounded-full min-h-[44px]" onClick={() => setRecommend(true)}><ThumbsUp className="h-4 w-4 mr-1.5" /> Ja</Button>
                  <Button type="button" variant={recommend === false ? "default" : "outline"} className="rounded-full min-h-[44px] px-6" onClick={() => setRecommend(false)}>Nej</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Bifoga ett intyg (valfritt)</Label>
                <p className="text-xs text-muted-foreground">T.ex. ett arbetsintyg eller betyg. PDF, bild eller Word – max 25 MB.</p>
                {uploads.length > 0 && (
                  <ul className="space-y-1.5" data-testid="uploaded-files">
                    {uploads.map((f) => (
                      <li key={f.id} className="flex items-center gap-2 text-sm rounded-lg border border-border bg-muted/30 px-3 py-2">
                        {f.preview
                          ? <img src={f.preview} alt="" className="h-10 w-10 rounded object-cover border border-border shrink-0" data-testid="attachment-thumb" />
                          : <FileText className="h-4 w-4 text-primary shrink-0" />}
                        <span className="truncate flex-1">{f.filename}</span>
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                      </li>
                    ))}
                  </ul>
                )}
                <label className="inline-flex">
                  <input type="file" className="sr-only" accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.doc,.docx" onChange={onUpload} data-testid="attachment-input" />
                  <span className={`inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm cursor-pointer hover:bg-muted transition-colors min-h-[44px] ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} {uploads.length ? "Ladda upp ett till" : "Välj fil"}
                  </span>
                </label>
              </div>

              <Button data-testid="submit-reference-btn" className="w-full rounded-full h-11" onClick={submit} disabled={busy}>
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Skicka referens
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
